import React from "react";
import { CheckCircle, WarningCircle, XCircle } from "@phosphor-icons/react";
import { DomainTestLeads } from "./DomainTestLeads";

const ICON = {
  pass: [CheckCircle, "text-emerald-700"],
  warn: [WarningCircle, "text-amber-600"],
  fail: [XCircle, "text-red-600"],
};
const LINK = "text-[10px] uppercase tracking-widest underline hover:no-underline";

export function Status({ s, children, testId }) {
  const [Icon, cls] = ICON[s] || ICON.warn;
  return (
    <span className={`inline-flex items-start gap-1 ${cls}`} data-testid={testId} data-status={s}>
      <Icon size={14} weight="bold" className="mt-[2px] shrink-0" />
      <span>{children}</span>
    </span>
  );
}

function PageRow({ p, testId }) {
  return (
    <li className="flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-zinc-200 py-1.5 text-[12px]" data-testid={testId}>
      <Status s={p.status} testId={`${testId}-status`}><b>{p.title}</b> · {p.detail}</Status>
      <span className="text-zinc-600" data-testid={`${testId}-layout`}>Thank-you: <b>{p.layout}</b></span>
      {p.url && <a href={p.url} target="_blank" rel="noopener noreferrer" className={LINK} data-testid={`${testId}-open`}>Open page</a>}
      {p.thank_you_url && <a href={p.thank_you_url} target="_blank" rel="noopener noreferrer" className={LINK} data-testid={`${testId}-ty`}>Open thank-you</a>}
    </li>
  );
}

function BuyerBlock({ b, canTest }) {
  const id = `domain-check-buyer-${b.code}`;
  return (
    <div className="border-2 border-black p-3" data-testid={id}>
      <div className="flex flex-wrap items-center gap-2">
        <Status s={b.status} testId={`${id}-status`}><b className="text-black">{b.firm_name}</b></Status>
        {b.is_test && <span className="border border-black bg-amber-200 px-1 text-[9px] uppercase">Test</span>}
        <span className="font-mono text-[10px] text-zinc-500">?b={b.code}</span>
      </div>
      <div className="mt-1 space-y-0.5 text-[12px]">
        <div><Status s={b.profile.status} testId={`${id}-profile`}>{b.profile.detail}</Status></div>
        <div><Status s={b.delivery.status} testId={`${id}-delivery`}>{b.delivery.detail}</Status></div>
      </div>
      <ul className="mt-2">{b.pages.map((p) => <PageRow key={p.key} p={p} testId={`${id}-page-${p.key}`} />)}</ul>
      {!b.pages.length && <p className="mt-2 text-[12px] text-zinc-500">No pages send this buyer leads yet.</p>}
      {canTest && <DomainTestLeads b={b} />}
    </div>
  );
}

export function DomainCheckResults({ data, canTest }) {
  const s = data.summary;
  return (
    <div className="space-y-3" data-testid="domain-check-results">
      <div className="border-2 border-black bg-zinc-50 p-3 text-[12px]">
        <div><Status s={data.connected.status} testId="domain-check-connected">{data.connected.detail}</Status></div>
        {data.connected.thank_you && (
          <div><Status s={data.connected.thank_you.status} testId="domain-check-thank-you">Thank-you page on {data.domain}: {data.connected.thank_you.detail}</Status></div>
        )}
        <div className="mt-1 text-zinc-600" data-testid="domain-check-summary">
          {s.buyers} buyer{s.buyers === 1 ? "" : "s"} · {s.pages} page{s.pages === 1 ? "" : "s"}: {s.pass} open, {s.warn} not live, {s.fail} failed
          {" · "}checked {new Date(data.checked_at).toLocaleTimeString()}
        </div>
      </div>
      {!data.buyers.length && <p className="text-sm text-zinc-600" data-testid="domain-check-empty">No buyer pages to check here.</p>}
      {data.buyers.map((b) => <BuyerBlock key={b.page_id} b={b} canTest={canTest} />)}
      <p className="text-[10.5px] text-zinc-500">
        &ldquo;Opens&rdquo; means {data.domain || "the landing domain"} serves the page. Whether it&apos;s live, its thank-you layout
        and the delivery come from your settings. Nothing is sent unless you press the test lead button.
      </p>
    </div>
  );
}
